import type { ColumnDef } from '@tanstack/react-table';
import type { CategoryTableRow } from '@/types/analyticsV2';

/**
 * Format a count + optional percent pair inline: "98  55 %" where the count
 * is the primary value and the percent renders in light grey to its right.
 * Inlined rather than a separate component so this file exports only
 * non-component values (keeps Fast Refresh happy).
 */
const renderCountPercent = (count: number | null, percent: number | null) => (
  <div className='flex items-baseline gap-2'>
    <span>{count ?? 0}</span>
    {percent !== null ? (
      <span className='text-[#9ca3af] text-[12px]'>{percent.toFixed(0)} %</span>
    ) : null}
  </div>
);

/**
 * Format the Avg AI Response /ticket column. Backend sends the value as a
 * plain ratio (not multiplied by 100), so we render with two decimal places
 * to match the Figma mock.
 */
const fmtRatio = (n: number | null): string =>
  n === null || n === undefined ? '-' : n.toFixed(2);

export const v2CategoriesColumns: ColumnDef<CategoryTableRow>[] = [
  {
    accessorKey: 'category',
    header: 'Category',
    size: 220,
    cell: ({ row }) => (
      <div className='flex items-center gap-2'>
        <div className='h-4 w-[2px] rounded-full bg-[#d1d5db]' />
        <span className='text-[#2b2e34]'>{row.original.category}</span>
      </div>
    ),
  },
  {
    accessorKey: 'tickets',
    header: 'Tickets',
    size: 120,
  },
  {
    accessorKey: 'avgAiResponsesPerTicket',
    header: 'Avg. AI Response /ticket',
    size: 180,
    cell: ({ row }) => <span>{fmtRatio(row.original.avgAiResponsesPerTicket)}</span>,
  },
  {
    id: 'withAi',
    header: 'With AI',
    size: 140,
    cell: ({ row }) =>
      renderCountPercent(row.original.withAi.count, row.original.withAi.percent),
  },
  {
    id: 'automated',
    header: 'Automated',
    size: 140,
    cell: ({ row }) =>
      renderCountPercent(row.original.automated.count, row.original.automated.percent),
  },
  {
    id: 'productivity',
    header: 'Productivity',
    size: 140,
    cell: () => <span className='text-[#9ca3af]'>{'—'}</span>,
  },
];
